import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import CurrencyInput from "react-currency-input-field";
import { CURRENCY_SYMBOL_MAP } from "../utils/constants";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faWallet, faSync, faLock } from "@fortawesome/free-solid-svg-icons";

function BatchUpdateBalanceForm({ accounts, onUpdateSuccess }) {
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [syncSuccess, setSyncSuccess] = useState(null);
  const [balances, setBalances] = useState({});
  const [note, setNote] = useState("");
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const initialBalances = {};
      accounts.forEach((account) => {
        initialBalances[account.id] = Math.round(account.balance);
      });
      setBalances(initialBalances);
    }
  }, [accounts]);

  const handleBalanceChange = (accountId, value) => {
    setBalances((prevBalances) => ({
      ...prevBalances,
      [accountId]: value,
    }));
  };

  const handleMonobankSync = async () => {
    setSyncLoading(true);
    setSyncError(null);
    setSyncSuccess(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/accounts/sync-monobank-balances`
      );

      const { updatedAccounts, message } = response.data;

      updatedAccounts.forEach((updatedAccount) => {
        setBalances((prevBalances) => ({
          ...prevBalances,
          [updatedAccount.id]: Math.round(updatedAccount.balance),
        }));
      });

      // Translate common messages or use the one from API if tailored
      const translatedMessage = updatedAccounts.length > 0 
        ? t("syncSuccess", `Синхронізовано ${updatedAccounts.length} рахунків`)
        : t("syncNoChanges", "Змін не виявлено або немає підключених рахунків");

      setSyncSuccess(translatedMessage);

      setTimeout(() => setSyncSuccess(null), 3000);

      // Removed onUpdateSuccess() to only persist on final submission
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      setSyncError(t("syncError", `Помилка синхронізації: ${errorMessage}`));
      setTimeout(() => setSyncError(null), 3000);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const updates = Object.keys(balances).map((accountId) => ({
      id: accountId,
      balance: parseFloat(balances[accountId]) || 0,
    }));

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/accounts/batch-update-balance`, {
        updates,
        note,
        reporter: user.name.split(" ")[0].charAt(0).toUpperCase() + user.name.split(" ")[0].slice(1),
        locale: i18n.language,
      });

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      setNote("");
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!accounts || accounts.length === 0) {
    return <div className="text-center py-4 text-gray-500">{t("loadingAccounts")}</div>;
  }

  const hasLinkedAccounts = accounts.some((account) => account.monobankAccountId);
  const headerActionsTarget = document.getElementById("header-actions");

  return (
    <div className="card bg-white dark:bg-dark-surface p-4 max-w-3xl lg:max-w-5xl mx-auto transition-colors overflow-hidden">
      <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
        <FontAwesomeIcon icon={faWallet} className="text-primary-500" />
        {t("updateAllBalances")}
      </h2>

      {hasLinkedAccounts && headerActionsTarget && createPortal(
        <button
          type="button"
          onClick={handleMonobankSync}
          disabled={syncLoading}
          className={`p-3 rounded-full flex items-center justify-center transition-all ${
            syncLoading 
              ? "text-gray-400 bg-gray-100 dark:bg-gray-800" 
              : "text-primary-600 bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30 shadow-sm"
          }`}
          title={t("syncFromMonobank", "Синхронізувати з Monobank")}
        >
          <FontAwesomeIcon icon={faSync} className={syncLoading ? "animate-spin" : ""} />
        </button>,
        headerActionsTarget
      )}

      <AnimatePresence>
        {(syncSuccess || syncError || error) && (
          <motion.div
            initial={{ height: 0, opacity: 0, marginBottom: 0 }}
            animate={{ height: "auto", opacity: 1, marginBottom: 16 }}
            exit={{ height: 0, opacity: 0, marginBottom: 0 }}
            className="overflow-hidden"
          >
            {syncSuccess && (
              <div className="text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center font-medium">
                {syncSuccess}
              </div>
            )}
            {syncError && (
              <div className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-center font-medium">
                {syncError}
              </div>
            )}
            {error && (
              <div className="text-red-500 bg-red-50 p-3 rounded-lg text-center font-medium">
                {t("error")}: {error}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit}>
        <div className="space-y-8">
          {/* Serhii's Accounts */}
          <div>
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-3 border-b dark:border-gray-700 pb-2">{t("serhiiAccountsHeading")}</h3>
            <div className="space-y-1">
              {accounts
                .filter((account) => account.holder === "Serhii")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((account) => {
                  const isLinked = !!account.monobankAccountId;
                  return (
                    <div
                      key={account.id}
                      className="flex items-center justify-between pl-2 pr-1 py-1 bg-gray-50 dark:bg-dark-bg rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <label htmlFor={`balance-${account.id}`} className="text-base font-medium text-gray-700 dark:text-gray-300 w-2/3 flex items-center gap-2">
                        {account.name}
                        {isLinked && (
                          <FontAwesomeIcon 
                            icon={faLock} 
                            className="text-primary-500 text-xs" 
                            title={t("linkedToMonobank", "Підключено до Monobank")}
                          />
                        )}
                      </label>
                      <CurrencyInput
                        id={`balance-${account.id}`}
                        name={`balance-${account.id}`}
                        value={balances[account.id] !== undefined ? balances[account.id] : ""}
                        onValueChange={(value) => handleBalanceChange(account.id, value)}
                        className={`w-1/3 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-right font-bold focus:ring-2 focus:ring-primary-100 outline-none transition-all ${
                          isLinked
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                            : "bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                        }`}
                        placeholder={t("enterNewBalance")}
                        decimalsLimit={0}
                        suffix={` ${CURRENCY_SYMBOL_MAP[account.currency]}`}
                        readOnly={isLinked}
                      />
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Kateryna's Accounts */}
          <div>
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1 border-b dark:border-gray-700 pb-2">{t("katerynaAccountsHeading")}</h3>
            <div className="space-y-1">
              {accounts
                .filter((account) => account.holder === "Kateryna")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((account) => {
                  const isLinked = !!account.monobankAccountId;
                  return (
                    <div
                      key={account.id}
                      className="flex items-center justify-between pl-2 pr-1 py-1 bg-gray-50 dark:bg-dark-bg rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <label htmlFor={`balance-${account.id}`} className="text-base font-medium text-gray-700 dark:text-gray-300 w-2/3 flex items-center gap-2">
                        {account.name}
                        {isLinked && (
                          <FontAwesomeIcon 
                            icon={faLock} 
                            className="text-primary-500 text-xs" 
                            title={t("linkedToMonobank", "Підключено до Monobank")}
                          />
                        )}
                      </label>
                      <CurrencyInput
                        id={`balance-${account.id}`}
                        name={`balance-${account.id}`}
                        value={balances[account.id] !== undefined ? balances[account.id] : ""}
                        onValueChange={(value) => handleBalanceChange(account.id, value)}
                        className={`w-1/3 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-right font-bold focus:ring-2 focus:ring-primary-100 outline-none transition-all ${
                          isLinked
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                            : "bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                        }`}
                        placeholder={t("enterNewBalance")}
                        decimalsLimit={0}
                        suffix={` ${CURRENCY_SYMBOL_MAP[account.currency]}`}
                        readOnly={isLinked}
                      />
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <label htmlFor="note" className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-2">
            {t("note")}
          </label>
          <textarea
            id="note"
            rows="3"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full p-4 bg-gray-50 dark:bg-dark-bg border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all resize-none dark:text-white"
            placeholder={t("addANote")}
            required
          ></textarea>
        </div>

        <button
          type="submit"
          className="btn-primary w-full mt-6 py-4 rounded-xl flex items-center justify-center gap-2 font-bold text-lg shadow-lg shadow-primary-500/30"
          disabled={loading}
        >
          {loading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} />
              {t("updateAllBalances")}
            </>
          )}
        </button>
      </form>
    </div>
  );
}

export default BatchUpdateBalanceForm;
